/** Desconto aplicado ao plano anual (calculado sobre 12 × preço mensal). */
export const ANNUAL_DISCOUNT_PERCENTAGE = 17;

const formatterCache = new Map();

function getNumberFormatter(locale) {
  if (!formatterCache.has(locale)) {
    formatterCache.set(
      locale,
      new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    );
  }
  return formatterCache.get(locale);
}

function formatAmountOnly(amount, currency) {
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'pt-PT' : 'en-US';
  const n = getNumberFormatter(locale).format(amount);
  if (currency === 'BRL') return `R$ ${n}`;
  if (currency === 'EUR') return `${n} €`;
  return `US$ ${n}`;
}

/**
 * @param {number} monthlyAmount
 */
export function computeAnnualPricing(monthlyAmount) {
  const fullAnnual = monthlyAmount * 12;
  const discountedAnnual = Math.round(fullAnnual * (1 - ANNUAL_DISCOUNT_PERCENTAGE / 100));
  const savings = fullAnnual - discountedAnnual;
  const equivalentMonthly = Math.round(discountedAnnual / 12);
  return { fullAnnual, discountedAnnual, savings, equivalentMonthly };
}

function isEnglish(language) {
  return language === 'en' || (language && !String(language).startsWith('pt'));
}

export function formatPlanAnnualSavingsLine(savings, currency, language) {
  const amount = formatAmountOnly(savings, currency);
  if (language === 'pt-PT') return `Economize ${amount} ao escolher o plano anual`;
  if (language === 'pt-BR') return `Economize ${amount} ao escolher o plano anual`;
  return `Save ${amount} with the annual plan`;
}

export function formatPlanEquivalentMonthlyLine(equivalentMonthly, currency, language) {
  const amount = formatAmountOnly(equivalentMonthly, currency);
  if (isEnglish(language)) return `Equivalent to ${amount}/mo`;
  return `Equivale a ${amount}/mês`;
}

/**
 * @param {{ monthly?: Record<string, number>, plus?: boolean }} plan
 * @param {string} currency
 * @param {'monthly'|'annual'} cycle
 * @param {string} language
 * @param {(amount: number, currency: string, cycle: string, opts: object) => string} formatPrice
 */
export function resolvePlanPrice({ plan, currency, cycle, language, formatPrice }) {
  if (!plan) return null;
  const monthlyAmount = plan.monthly?.[currency];
  if (monthlyAmount == null) return null;

  const opts = { plus: !!plan.plus, language };

  if (cycle === 'monthly') {
    return {
      amount: monthlyAmount,
      label: formatPrice(monthlyAmount, currency, cycle, opts)
    };
  }

  const { discountedAnnual, savings, equivalentMonthly } = computeAnnualPricing(monthlyAmount);
  return {
    amount: discountedAnnual,
    label: formatPrice(discountedAnnual, currency, 'annual', opts),
    savingsLine: formatPlanAnnualSavingsLine(savings, currency, language),
    equivalentMonthlyLine: formatPlanEquivalentMonthlyLine(equivalentMonthly, currency, language)
  };
}
